
import { Component, Prop, Vue } from 'vue-property-decorator';
import { ALLERGENS_LIST, DIETS_LIST } from '@/utils/constants';
import { countActiveFilters } from '@/utils/bulkJob';
import { decodeString, decodeTag } from '@/utils/decoding';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import SelectComponent from '@/components/shared/Select.vue';
import ChipContainer from '@/components/shared/ChipContainer.vue';
import TagPreview from '@/components/shared/TagPreview.vue';
import TextField from '@/components/shared/TextField.vue';

/*
 * The yes/no filter groups, in display order. They differ only in their labels, so the template
 * renders them from this list rather than repeating the checkbox pair three times. Labels are the
 * trailing part of the key; the template supplies the shared prefix.
 */
const TOGGLE_GROUPS: BulkFilterToggleGroup[] = [
	{ key: 'eventPackage', id: 'event-package', label: 'menu_type_label', yesLabel: 'event', noLabel: 'retail' },
	{ key: 'featured', id: 'featured', label: 'featured_label', yesLabel: 'yes', noLabel: 'no' },
	{ key: 'upsell', id: 'upsell', label: 'upsell_label', yesLabel: 'yes', noLabel: 'no' }
];

/**
 * Identity of a tag. Name alone is not enough: the location-tags list is a union across locations, so
 * two locations can each define a tag of the same name with a different icon or colour.
 *
 * @param {MenuItemTag} tag
 * @return {string}
 */
function tagKey(tag: MenuItemTag): string {
	return `${tag.name}|${tag.icon ?? ''}|${tag.color ?? ''}`;
}

/**
 * Stable string form of a filter set, so the draft can be compared against what is applied without
 * key or value order mattering.
 *
 * @param {BulkMenuFilters} filters
 * @return {string}
 */
function serializeFilters(filters: BulkMenuFilters): string {
	return Object.keys(filters)
		.sort()
		.map((key: string) => {
			const value: unknown = filters[key as keyof BulkMenuFilters];
			if (!Array.isArray(value)) {
				return `${key}:${value}`;
			}
			const entries: string[] = value.map(entry => typeof entry === 'string' ? entry : tagKey(entry));
			return `${key}:${entries.sort().join(',')}`;
		})
		.join('|');
}

/**
 * The value a yes/no group contributes. Both boxes ticked (or neither) means the group narrows
 * nothing, which is `undefined` — not `false`, which legitimately means "only the ones that aren't".
 *
 * @param {{ yes: boolean; no: boolean }} group
 * @return {boolean | undefined}
 */
function toggleFilter(group: { yes: boolean; no: boolean }): boolean | undefined {
	return group.yes === group.no ? undefined : group.yes;
}

/**
 * Drop the filters that were left off, so every key the draft keeps is an active filter.
 *
 * @param {BulkMenuFilters} draft
 * @return {BulkMenuFilters}
 */
function omitUnsetFilters(draft: BulkMenuFilters): BulkMenuFilters {
	Object.keys(draft).forEach((key: string) => {
		if (draft[key as keyof BulkMenuFilters] === undefined) {
			delete draft[key as keyof BulkMenuFilters];
		}
	});
	return draft;
}

@Component<BulkFiltersModal>({
	components: {
		StandardModal,
		SelectComponent,
		ChipContainer,
		TagPreview,
		TextField
	}
})
export default class BulkFiltersModal extends Vue {
	@Prop({ type: Boolean, required: false, default: false }) private showModal!: boolean;
	@Prop({ type: Object, required: false, default: () => ({}) }) private filters!: BulkMenuFilters;
	@Prop({ type: Array, required: false, default: () => [] }) private locationTags!: MenuItemTag[];
	@Prop({ type: Boolean, required: false, default: false }) private showSku!: boolean;
	@Prop({ type: Boolean, required: false, default: false }) private showEventPackage!: boolean;

	private readonly allergenOptions = ALLERGENS_LIST;
	private readonly dietOptions = DIETS_LIST;
	private sku: string = this.filters.sku ?? '';
	/*
	 * Held as tag keys, not names — see tagKey above; the payload maps them back to the full objects.
	 */
	private selectedTags: string[] = (this.filters.tags ?? []).map(tag => tagKey(tag));
	private selectedAllergens: string[] = [...(this.filters.allergens ?? [])];
	private selectedDiets: string[] = [...(this.filters.diets ?? [])];
	private includeNoTags: boolean = !!this.filters.include_no_tags;
	private includeNoAllergens: boolean = !!this.filters.include_no_allergens;
	private includeNoDiets: boolean = !!this.filters.include_no_diets;
	private allergensExpanded: boolean = !!this.filters.allergens?.length;
	/*
	 * A single value selected means "filter by it"; both or neither means "don't filter".
	 */
	private toggles: Record<BulkFilterToggleKey, { yes: boolean; no: boolean }> = {
		eventPackage: { yes: this.filters.event_package !== false, no: this.filters.event_package !== true },
		featured: { yes: this.filters.featured !== false, no: this.filters.featured !== true },
		upsell: { yes: this.filters.upsell !== false, no: this.filters.upsell !== true }
	};

	/**
	 * Menu type is Bowlero-only, so it drops out of the list when the selection has no Bowlero
	 * location. Order is what decides which group renders flush against the top.
	 *
	 * @return {BulkFilterToggleGroup[]}
	 */
	private get visibleToggleGroups(): BulkFilterToggleGroup[] {
		return TOGGLE_GROUPS.filter(group => group.key !== 'eventPackage' || this.showEventPackage);
	}

	/*
	 * Each option carries its decoded tag so the select's slots can render a preview straight from
	 * the item; `rawTagByKey` keeps the untouched objects the backend expects back.
	 */
	private get tagOptions(): { text: string; value: string; tag: MenuItemTag }[] {
		return this.locationTags.map(tag => ({ text: decodeString(tag.name), value: tagKey(tag), tag: decodeTag(tag) }));
	}

	private get rawTagByKey(): Record<string, MenuItemTag> {
		const map: Record<string, MenuItemTag> = {};
		this.locationTags.forEach(tag => {
			map[tagKey(tag)] = tag;
		});
		return map;
	}

	/**
	 * Build the filter payload from the form. Each entry reads as "the value, or undefined when this
	 * filter is off", and the ones left off are dropped in a single pass at the end.
	 *
	 * @return {BulkMenuFilters}
	 */
	private get draftFilters(): BulkMenuFilters {
		const tags: MenuItemTag[] = this.selectedTags.map(key => this.rawTagByKey[key]).filter(Boolean);

		return omitUnsetFilters({
			sku: this.showSku && this.sku ? this.sku : undefined,
			event_package: this.showEventPackage ? toggleFilter(this.toggles.eventPackage) : undefined,
			featured: toggleFilter(this.toggles.featured),
			upsell: toggleFilter(this.toggles.upsell),
			tags: tags.length ? tags : undefined,
			allergens: this.selectedAllergens.length ? [...this.selectedAllergens] : undefined,
			diets: this.selectedDiets.length ? [...this.selectedDiets] : undefined,
			include_no_tags: this.includeNoTags || undefined,
			include_no_allergens: this.includeNoAllergens || undefined,
			include_no_diets: this.includeNoDiets || undefined
		});
	}

	private get activeFilterCount(): number {
		return countActiveFilters(this.draftFilters);
	}

	// Reset stays available whenever there is something to reset: the draft or the applied filters.
	private get disableReset(): boolean {
		return !this.activeFilterCount && !Object.keys(this.filters).length;
	}

	// Apply is for committing a change — including clearing filters that are currently applied.
	private get disableApply(): boolean {
		return serializeFilters(this.draftFilters) === serializeFilters(this.filters);
	}

	private get applyButtonText(): string {
		const count: number = this.activeFilterCount;
		return this.$t('multi_location_settings.modals.filters.apply') + (count ? ` (${count})` : '');
	}

	/**
	 * Return every control to its default (no filter) state and apply immediately.
	 *
	 * @return {void}
	 */
	private resetFilters(): void {
		this.sku = '';
		Object.values(this.toggles).forEach(group => {
			group.yes = true;
			group.no = true;
		});
		this.selectedTags = [];
		this.selectedAllergens = [];
		this.selectedDiets = [];
		this.includeNoTags = false;
		this.includeNoAllergens = false;
		this.includeNoDiets = false;
		this.$emit('apply', {});
	}
}
